import { useEffect, useRef, useState } from "react";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * KYCCard — gates crypto cashout flows on the /cashout page.
 *
 * Lifecycle:
 *   - Polls /api/kyc/status on mount + every 3s while a pending inquiry is open.
 *   - Renders a CTA when status is 'unverified' or 'rejected'.
 *   - Renders a "verification in progress" state when 'pending' (also opens
 *     the provider embed URL in a new tab if it's an external URL).
 *   - Renders a green "Verified" pill + monthly cap usage bar when 'approved'.
 *
 * Mock-mode handling: backend auto-approves after ~2s; we'll catch the flip
 * on the next poll and toast a success notification.
 */
export default function KYCCard({ onApproved }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const lastStatusRef = useRef(null);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/kyc/status");
      setStatus(data);
      if (lastStatusRef.current && lastStatusRef.current !== "approved"
          && data.status === "approved") {
        toast.success("Identity verified — you can now add a crypto wallet.");
        if (onApproved) onApproved();
      }
      lastStatusRef.current = data.status;
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    fetchStatus();
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, []);

  // Idempotent backup sync: if we just came back from Persona's hosted flow
  // (URL has ?kyc=callback), pull the latest inquiry state from Persona once
  // so the UI updates instantly even if the webhook hasn't landed yet.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("kyc") !== "callback") return;
    (async () => {
      try {
        await api.post("/kyc/refresh");
      } catch (e) { /* noop — fall back to polling */ }
      fetchStatus();
      // Clean the query string so a refresh doesn't re-trigger.
      const url = new URL(window.location.href);
      url.searchParams.delete("kyc");
      window.history.replaceState({}, "", url.toString());
    })();
  }, []);

  // Keep polling every 3s while pending, stop otherwise.
  useEffect(() => {
    if (!status) return;
    if (status.status === "pending" && !pollRef.current) {
      pollRef.current = setInterval(fetchStatus, 3000);
    } else if (status.status !== "pending" && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [status?.status]);

  const startKyc = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/kyc/start");
      toast.info("Identity verification started.");
      // External provider: open the embed URL in a new tab.
      // Mock provider: stay on page, polling will reveal the flip.
      if (data.embedUrl && !data.mock && data.embedUrl.startsWith("http")) {
        window.open(data.embedUrl, "_blank", "noopener,noreferrer");
      }
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start verification");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  if (status.status === "approved") {
    const cap = status.monthlyCapUsd || 0;
    const used = status.monthlyUsedUsd || 0;
    const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
    return (
      <div className="parchment p-4 border-secondary/30 bg-secondary/[0.04] flex flex-col gap-2"
           data-testid="kyc-card-approved">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-secondary" strokeWidth={2} />
          <span className="label-mystic text-secondary">Identity Verified</span>
          <span className="ml-auto text-[11px] text-foreground/55 uppercase tracking-wider">
            Tier: {status.tier || "basic"}
          </span>
        </div>
        <p className="text-xs text-foreground/70">
          Monthly crypto cashout cap: <b className="text-foreground">${cap.toFixed(2)}</b> ·
          used <b>${used.toFixed(2)}</b> · remaining <b className="text-secondary">${status.monthlyRemainingUsd.toFixed(2)}</b>
        </p>
        <div className="h-1 bg-foreground/10 rounded-full overflow-hidden">
          <div className="h-full bg-secondary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  if (status.status === "pending") {
    const canReopen = !status.mock && status.embedUrl && status.embedUrl.startsWith("http");
    return (
      <div className="parchment p-4 border-primary/30 bg-primary/[0.04] flex items-center gap-3 flex-wrap"
           data-testid="kyc-card-pending">
        <Loader2 size={16} className="text-primary animate-spin shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <div className="label-mystic text-primary">Verification in progress</div>
          <div className="text-xs text-foreground/65 mt-0.5">
            {status.mock
              ? "Our mock identity provider is auto-approving you (takes ~2 seconds in dev mode)."
              : "Complete the identity check in the open Persona tab — we'll unlock crypto cashouts once it's approved."}
          </div>
        </div>
        {canReopen && (
          <button
            type="button"
            onClick={() => window.open(status.embedUrl, "_blank", "noopener,noreferrer")}
            className="px-4 py-2 rounded-sm border border-primary/50 text-primary hover:bg-primary/10 text-xs uppercase tracking-wider shrink-0"
            data-testid="kyc-reopen-button"
          >
            Reopen verification
          </button>
        )}
      </div>
    );
  }

  // unverified | rejected | expired
  return (
    <div className="parchment p-5 border-secondary/30 flex flex-col sm:flex-row sm:items-center gap-4"
         data-testid="kyc-card-cta">
      <div className="flex items-start gap-3 flex-1">
        <ShieldAlert size={20} className="text-secondary shrink-0 mt-1" strokeWidth={1.75} />
        <div>
          <div className="label-mystic text-secondary mb-1">Verify identity for crypto cashouts</div>
          <p className="text-sm text-foreground/75 leading-relaxed">
            One-time ID check unlocks USDC cashouts on Base — up to <b className="text-foreground">$500/month</b>.
            Takes ~2 minutes. Card &amp; PayPal cashouts work without this.
          </p>
          {status.status === "rejected" && (
            <p className="text-xs text-destructive mt-1">
              Last verification was rejected — you can retry now.
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={startKyc}
        disabled={busy}
        className="px-5 py-2.5 rounded-sm bg-secondary text-background hover:bg-secondary/90 text-sm uppercase tracking-wider disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
        data-testid="kyc-start-button"
      >
        {busy ? "Starting…" : "Start verification"}
      </button>
    </div>
  );
}
